/**
 * Batch Allocations Tab Component
 * Displays orders that have allocated this batch
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-002
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { BatchResponse, BatchAllocation } from '@/types/inventory';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { ExternalLink, Package } from 'lucide-react';

interface BatchAllocationsTabProps {
  batch: BatchResponse;
}

// Status badge configuration
const OrderStatusConfig: Record<string, { color: string; bgColor: string }> = {
  CONFIRMED: { color: 'text-green-700', bgColor: 'bg-green-100' },
  PENDING: { color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  SHIPPED: { color: 'text-blue-700', bgColor: 'bg-blue-100' },
  DELIVERED: { color: 'text-green-700', bgColor: 'bg-green-100' },
  CANCELLED: { color: 'text-red-700', bgColor: 'bg-red-100' },
};

export function BatchAllocationsTab({ batch }: BatchAllocationsTabProps) {
  // Get allocations from batch response
  const allocations: BatchAllocation[] = batch.allocations || [];

  // Check if batch has allocations from the response
  const hasAllocations = allocations.length > 0;

  return (
    <div className="space-y-6">
      {/* Allocation Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Allocation Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-600 font-medium mb-1">
                Current Quantity
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {batch.currentQuantity}
              </div>
              <div className="text-xs text-gray-500">
                {batch.quantityUnit}
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs text-blue-600 font-medium mb-1">
                Allocated
              </div>
              <div className="text-2xl font-bold text-blue-900">
                {batch.allocatedQuantity}
              </div>
              <div className="text-xs text-blue-600">
                {batch.quantityUnit}
              </div>
            </div>

            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-green-600 font-medium mb-1">
                Available
              </div>
              <div className="text-2xl font-bold text-green-900">
                {batch.availableQuantity}
              </div>
              <div className="text-xs text-green-600">
                {batch.quantityUnit}
              </div>
            </div>

            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="text-xs text-orange-600 font-medium mb-1">
                Total Orders
              </div>
              <div className="text-2xl font-bold text-orange-900">
                {allocations.length}
              </div>
              <div className="text-xs text-orange-600">
                Orders
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Allocations Table */}
      {hasAllocations ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5" aria-hidden="true" />
              Orders with Allocations ({allocations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table role="table" aria-label="Orders with batch allocations">
                <caption className="sr-only">
                  List of {allocations.length} orders with allocations from this batch
                </caption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Order Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allocations.map((allocation) => {
                    const statusConfig = OrderStatusConfig[allocation.orderStatus] || {
                      color: 'text-gray-700',
                      bgColor: 'bg-gray-100',
                    };

                    return (
                      <TableRow key={allocation.orderId} className="hover:bg-gray-50">
                        <TableCell className="font-semibold text-blue-600">
                          {allocation.orderNumber}
                        </TableCell>
                        <TableCell>{allocation.customerName}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {allocation.quantity} {batch.quantityUnit}
                        </TableCell>
                        <TableCell>
                          {allocation.orderDate
                            ? format(new Date(allocation.orderDate), 'MMM dd, yyyy')
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={cn(
                              statusConfig.bgColor,
                              statusConfig.color,
                              'border-none'
                            )}
                          >
                            {allocation.orderStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="pt-8">
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Allocations
              </h3>
              <p className="text-gray-600 mb-4">
                This batch has not been allocated to any orders yet.
              </p>
              <Button variant="outline">
                Create Order with this Batch
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Allocation Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Allocation Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">
              How Allocations Work
            </h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex gap-2">
                <span className="font-bold">•</span>
                <span>
                  When an order is created, it allocates a specific quantity from a batch
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">•</span>
                <span>
                  Allocated quantity is reserved and cannot be used by other orders
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">•</span>
                <span>
                  Available quantity = Current quantity - Allocated quantity
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">•</span>
                <span>
                  Only PESO compliant batches can be allocated to orders
                </span>
              </li>
            </ul>
          </div>

          {/* Allocation Constraints */}
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-900 mb-2">
              Allocation Constraints
            </h4>
            <ul className="space-y-2 text-sm text-amber-800">
              <li className="flex gap-2">
                <span className="font-bold">✓</span>
                <span>
                  Batch must be PESO compliant
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">✓</span>
                <span>
                  Batch must not be expired
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">✓</span>
                <span>
                  Sufficient available quantity required
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">✓</span>
                <span>
                  Quality grade should be A or B
                </span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
